import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PendingIcon from "@mui/icons-material/Pending";
import { Button, Popover } from "@mui/material";
import cx from "classnames";
import { uniqBy } from "lodash";
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from "react";

import DotElastic from "components/DotElastic";
import Guide from "components/Guide";
import ShotContext from "components/Shot/ShotContext";
import VideoPlayer, { VideoPlayerRefAttributes } from "components/VideoPlayer";
import { captureSnapshot } from "components/VideoPreview/video_preview_utils";
import {
  UserGuides,
  SERVICES,
  STATUSES,
  WINDOW_EVENTS,
} from "constants/constants";
import { formatLongText } from "utils";

import styles from "./ReviewPanel.module.scss";
import { Cutout } from "../../models/Cutout";

type ReviewPanelParams = object;

const ReviewPanel: React.FC<ReviewPanelParams> = (_) => {
  const {
    layerId,
    cutouts,
    shotCutouts,
    selectedShot,
    setImageData,
    service,
    setSelectedFrame,
    reviewLayer: layer,
    selectedCutout,
    setSelectedCutout,
  } = useContext(ShotContext);
  const defaultFps = selectedShot?.fps ?? 0;

  const [fps, setFps] = useState<number>(defaultFps);
  const [sortedCutouts, setSortedCutouts] = useState<Cutout[]>([]);
  const [markerPositions, setMarkerPositions] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const playerRef = useRef<VideoPlayerRefAttributes>(null);
  const sourceVideoRef = useRef<HTMLVideoElement>(null);
  const markerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [totalFrames, setTotalFrames] = useState(layer?.shot.frame_count || 0);
  const [videoWidth, setVideoWidth] = useState(0);
  const [videoHeight, setVideoHeight] = useState(0);

  useEffect(() => {
    workerRef.current = new Worker(
      new URL("../../worker.ts", import.meta.url),
      {
        type: "module",
      },
    );
    workerRef.current.onmessage = (ev) => {
      if (!layer || !selectedCutout) return;
      const { id, results, indexes, width, height } = ev.data;
      if (selectedCutout?.id === id) {
        const current = playerRef.current?.imageDatas || [];
        if (current.length === 0) {
          playerRef.current?.setWidthHeight([width, height]);
        }
        const datas = current.slice();
        results.forEach((bitMap, index) => {
          datas[indexes[index]] = bitMap;
        });
        playerRef.current?.setImageDatas(datas);
      }
    };

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [layer, selectedCutout]);

  useEffect(() => {
    if (!selectedShot || !layer) return;

    if (selectedCutout?.inference?.preview) {
      setTotalFrames(
        selectedCutout.inference.preview.frame_count ||
          selectedShot?.frame_count ||
          0,
      );
      playerRef.current?.setImageDatas([]);
      setVideoUrl(selectedCutout.inference.preview.url);

      if (selectedCutout.inference.preview.fps)
        setFps(selectedCutout.inference.preview.fps);
      return;
    }

    setTotalFrames(selectedShot.frame_count);
    setVideoUrl(selectedShot.video_url);
    setFps(defaultFps);
  }, [layer, selectedCutout, selectedShot, fps, defaultFps]);

  useEffect(() => {
    if (
      !layer ||
      !selectedCutout ||
      !selectedShot ||
      isLoading ||
      selectedCutout.inference?.preview
    ) {
      return;
    }

    if (selectedCutout.inference?.status === STATUSES.COMPLETED) {
      playerRef.current?.setImageDatas([]);
      workerRef.current?.postMessage({
        startIndex: 1,
        endIndex: layer.review_urls.length,
        reviewUrls: layer.review_urls,
        statuses: layer.statuses,
        selectedCutout,
        firstFrame: layer.shot.first_frame_number,
      });
    } else {
      const previewUrl =
        selectedCutout.preview_image_url || selectedCutout.image_url;
      if (previewUrl) {
        const datas: ImageBitmap[] = [];
        fetch(previewUrl)
          .then((response) => response.blob())
          .then((blob) => createImageBitmap(blob))
          .then((imageBitmap) => {
            datas[selectedCutout.frame - layer.shot.first_frame_number] =
              imageBitmap;
            playerRef.current?.seekToFrame(
              selectedCutout.frame - layer.shot.first_frame_number,
            );
            playerRef.current?.setImageDatas(datas);
          });
      }
    }
  }, [layer, selectedCutout, isLoading, selectedShot, setSelectedFrame]);

  const getStatusIcon = (id: number) => {
    if (layer?.statuses[id] === "FAILED") {
      return <ErrorIcon fontSize="small" color="error" />;
    }
    if (layer?.statuses[id] === "IN_PROGRESS") {
      return <PendingIcon fontSize="small" color="warning" />;
    }
    if (layer?.statuses[id] === "COMPLETED") {
      return <CheckCircleIcon fontSize="small" color="success" />;
    }
    return <div />;
  };

  useEffect(() => {
    const filteredCutouts = uniqBy(
      (service === SERVICES.TRACKING ? shotCutouts : cutouts).sort((c1, c2) => {
        if (c1.inference && c2.inference) {
          return c1.inference?.created > c2.inference?.created ? -1 : 1;
        }
        return 0;
      }),
      "frame",
    );
    setSortedCutouts(filteredCutouts);

    if (service === SERVICES.TRACKING && layer) {
      setSelectedCutout(
        filteredCutouts.length
          ? filteredCutouts.find((c) => c.layer_id === layer.id) ||
              filteredCutouts[filteredCutouts.length - 1]
          : null,
      );
    } else if (service === SERVICES.ROTOSCOPING) {
      if (layer) {
        setSelectedCutout((prev) => {
          if (prev && layer && prev.layer_id === layer.id) return prev;
          if (prev && filteredCutouts.some((c) => c.id === prev.id))
            return prev;
          return filteredCutouts.length ? filteredCutouts[0] : null;
        });
      }
    } else {
      setSelectedCutout(filteredCutouts.length ? filteredCutouts[0] : null);
    }
  }, [cutouts, layer, service, shotCutouts, setSelectedCutout]);

  useEffect(() => {
    playerRef.current?.setImageDatas([]);
    playerRef.current?.pause();
  }, [layerId]);

  const handleAnnotate = useCallback(
    (e: Event) => {
      if (selectedShot && playerRef.current && layer) {
        const sourceVideo = sourceVideoRef.current;
        if (!sourceVideo) return;

        const frame =
          (e as CustomEvent).detail?.frame ||
          playerRef.current.currentFrame + layer.shot.first_frame_number; // absolute frame number

        const handleSeeked = () => {
          setImageData(
            captureSnapshot(sourceVideo).toDataURL("image/jpeg", 1.0),
          );
          setSelectedFrame(frame);
          sourceVideo.removeEventListener("seeked", handleSeeked);
        };
        sourceVideo.addEventListener("seeked", handleSeeked);

        sourceVideo.currentTime =
          (frame - layer.shot.first_frame_number) /
            Math.round(selectedShot.frame_count / sourceVideo.duration) +
          0.0005; // relative frame number
      }
    },
    [selectedShot, layer, setImageData, setSelectedFrame],
  );

  useEffect(() => {
    window.addEventListener(WINDOW_EVENTS.ANNOTATE, handleAnnotate);
    return () => {
      window.removeEventListener(WINDOW_EVENTS.ANNOTATE, handleAnnotate);
    };
  }, [handleAnnotate]);

  // Drive the player to whatever cutout the context says is selected.
  useEffect(() => {
    if (!layer || !selectedCutout) return;
    if (selectedCutout.layer_id !== layer.id) return;
    playerRef.current?.setImageDatas([]);
    playerRef.current?.seekToFrame(
      selectedCutout.frame - layer.shot.first_frame_number,
    );
  }, [selectedCutout, layer]);

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [hoverCutout, setHoverCutout] = React.useState<Cutout | null>(null);

  const handleCutoutPopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    c: Cutout,
  ) => {
    setAnchorEl(event.currentTarget);
    setHoverCutout(c);
  };

  const handleCutoutPopoverClose = () => {
    setAnchorEl(null);
    setHoverCutout(null);
  };

  const getMarkerPositions = useCallback(() => {
    setSortedCutouts((sCutouts) => {
      if (!sCutouts.length || !layer) {
        return sCutouts;
      }
      const cMarks = sCutouts
        .map((cutOut) =>
          document.querySelector(
            `.MuiSlider-mark[data-index='${
              cutOut.frame - layer.shot.first_frame_number
            }']`,
          ),
        )
        .filter((mark) => !!mark);
      if (cMarks.length !== sCutouts.length) {
        if (markerTimeoutRef.current) {
          clearTimeout(markerTimeoutRef.current);
        }

        markerTimeoutRef.current = setTimeout(getMarkerPositions, 1000);
        return sCutouts;
      }
      setMarkerPositions(
        cMarks.map((cMark) => cMark?.getBoundingClientRect().left || 0),
      );
      return sCutouts;
    });
  }, [layer]);

  useEffect(() => {
    if (getMarkerPositions) {
      getMarkerPositions();
      window.addEventListener("resize", getMarkerPositions);
      return () => {
        window.removeEventListener("resize", getMarkerPositions);
        if (markerTimeoutRef.current) {
          clearTimeout(markerTimeoutRef.current);
          markerTimeoutRef.current = null;
        }
      };
    }
    return () => {};
  }, [getMarkerPositions]);

  useEffect(() => {
    setIsLoading(true);
  }, [layer]);

  useEffect(() => {
    if (isLoading) {
      handleCutoutPopoverClose();
    }
  }, [isLoading]);

  return (
    <div className={styles.container}>
      {isLoading && (
        <div className={styles.hintContainer}>
          <h4>Loading results...</h4>
          <DotElastic />
        </div>
      )}

      {selectedShot?.video_url && (
        <video
          ref={sourceVideoRef}
          src={selectedShot.video_url}
          muted
          crossOrigin="anonymous"
          loop
          preload="auto"
          style={{
            visibility: "hidden",
            width: "1px",
            height: "1px",
            position: "absolute",
          }}
          onLoadedMetadata={() => {
            setVideoWidth(sourceVideoRef.current?.videoWidth || 0);
            setVideoHeight(sourceVideoRef.current?.videoHeight || 0);
          }}
        />
      )}

      {layer && (
        <div className={styles.mainContainer}>
          {videoWidth && videoHeight && (
            <>
              {service === SERVICES.RETIME &&
                !!selectedCutout?.inference?.preview?.url &&
                !isLoading && (
                  <div className={styles.retimeOverlay}>Retimed output</div>
                )}
              <VideoPlayer
                ref={playerRef}
                videoURL={videoUrl}
                totalFrames={totalFrames}
                firstFrame={layer.shot.first_frame_number}
                setIsLoading={setIsLoading}
                fps={fps}
                startTime={layer.shot.start_time}
                color={layer.color}
                videoWidth={videoWidth}
                videoHeight={videoHeight}
                fillPreview={service === SERVICES.SMART_VECTORS}
                onPlay={() => {
                  if (selectedCutout)
                    playerRef.current?.seekToFrame(
                      selectedCutout.frame - layer.shot.first_frame_number,
                    );
                }}
                loop={
                  !(
                    service === SERVICES.TRACKING &&
                    !selectedCutout?.inference?.preview
                  )
                }
                isForReview
              />
            </>
          )}

          {!isLoading &&
            (service === SERVICES.ROTOSCOPING ||
              service === SERVICES.TRACKING ||
              service === SERVICES.SMART_VECTORS) && (
              <div className={styles.cutouts}>
                <div className={styles.cutOutLayerCon}>
                  <div className={styles.cutOutLayerName}>
                    {formatLongText(layer.name, 4)}
                  </div>
                  <div
                    className={styles.cutOutLayerLine}
                    style={{ backgroundColor: layer.color }}
                  />
                </div>
                {sortedCutouts.map((c, index) => {
                  if (markerPositions[index])
                    return (
                      <div
                        key={c.id}
                        style={{
                          left: `${markerPositions[index] - 6}px`,
                        }}
                        className={styles.markerCon}
                      >
                        <Guide
                          id={UserGuides.FirstCutout}
                          open={
                            index === 0 &&
                            !hoverCutout &&
                            (service === SERVICES.ROTOSCOPING ||
                              service === SERVICES.SMART_VECTORS)
                          }
                          title={
                            service === SERVICES.SMART_VECTORS
                              ? "ROI keyframe"
                              : "New annotation added!"
                          }
                          description={
                            service === SERVICES.SMART_VECTORS
                              ? "Hover for a preview of this frame's ROI. Click the dot to show or hide the region on the player."
                              : "Hover on this frame layer to view your annotation."
                          }
                          placement="top"
                          enableHoverListener
                        >
                          <Button
                            onMouseEnter={(e) => handleCutoutPopoverOpen(e, c)}
                            onMouseLeave={handleCutoutPopoverClose}
                            style={{
                              backgroundColor: layer.color,
                            }}
                            onClick={() => {
                              setSelectedCutout((prev) =>
                                prev?.id === c.id ? null : c,
                              );
                            }}
                            variant="contained"
                            size="small"
                            className={cx(
                              styles.cutoutIcon,
                              c.id === selectedCutout?.id &&
                                styles.cutoutIconActive,
                            )}
                          />
                        </Guide>
                      </div>
                    );
                  return null;
                })}
              </div>
            )}

          <Popover
            sx={{
              pointerEvents: "none",
              "& .MuiPaper-root": {
                backgroundColor: "#111113",
                borderRadius: "0px",
                border: "1px solid #CCCCDC1F",
                padding: "8px",
              },
            }}
            open={Boolean(anchorEl) && !isLoading}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            onClose={handleCutoutPopoverClose}
            disableRestoreFocus
          >
            <div className={styles.popoverButtons}>
              {getStatusIcon(hoverCutout?.inference?.id || -1)}
              <p>{hoverCutout && hoverCutout?.frame}</p>
            </div>
            <div className={styles.hoverImage}>
              {(hoverCutout?.preview_image_url || hoverCutout?.image_url) && (
                <img
                  className={styles.hoverImage}
                  src={
                    hoverCutout.preview_image_url || hoverCutout.image_url || ""
                  }
                  alt="cutout"
                  crossOrigin="anonymous"
                  onError={(
                    e: React.SyntheticEvent<HTMLImageElement, Event>,
                  ) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
            </div>
          </Popover>
        </div>
      )}
    </div>
  );
};

export default ReviewPanel;
